import { Component, computed, effect, inject, signal } from "@angular/core";
import { Router } from "@angular/router";
import { FiltersService } from "src/app/core/services/filters/filters-service";
import { VideoSearchStore } from "../../store/video-search-store";
import {
  FilterSet,
  FilterSetType,
} from "src/app/core/interfaces/filter-interface";
import { Loader } from "src/app/shared/components/loader/loader";
import { FilterSection } from "src/app/shared/components/filter-section/filter-section";
import { Button } from "src/app/shared/components/utils/button/button";
import { TranslocoModule } from "@jsverse/transloco";
import {
  CreateFavoriteFilterSearchRequestInterface,
  SearchType,
} from "src/app/core/interfaces/favorite-interface";
import { FavoritesService } from "../../services/favorites/favorites-service";
import { InputModal } from "src/app/shared/components/utils/input-modal/input-modal";
import { LanguageService } from "src/app/core/services/language-service";
import { FavoriteStore } from "src/app/features/store/favorite-store";
import { FavoriteSearchErrorModal } from "src/app/shared/components/favorite-search-error-modal/favorite-search-error-modal";
import { ErrorModalService } from "src/app/core/services/error-modal-service";

@Component({
  selector: "app-video-search-page",
  imports: [
    Loader,
    FilterSection,
    Button,
    TranslocoModule,
    InputModal,
    FavoriteSearchErrorModal,
  ],
  templateUrl: "./video-search-page.html",
})
export class VideoSearchPage {
  router = inject(Router);
  filtersService = inject(FiltersService);
  favoritesService = inject(FavoritesService);
  favoriteStore = inject(FavoriteStore);
  store = inject(VideoSearchStore);
  private readonly languageService = inject(LanguageService);
  private readonly errorModalService = inject(ErrorModalService);

  filters = signal<FilterSet[]>([]);

  branchesFiltersSelected = signal<FilterSet>(null);
  activitiesFiltersSelected = signal<FilterSet>(null);

  branchesEmpty = signal<boolean>(true);
  activitiesEmpty = signal<boolean>(true);
  isActivityFilterUsed = signal<boolean>(false);
  loading = signal<boolean>(false);
  isAddToFavoriteModalOpen = signal(false);
  isFavoriteSearchSuccessSaved = signal<boolean>(false);

  branchesFilters = computed(() =>
    this.filters().find((f) => f.filterSetType === FilterSetType.Sector)
  );

  activitiesFilters = computed(() =>
    this.filters().find((f) => f.filterSetType === FilterSetType.Activity)
  );

  onBranchesEmpty(isEmpty: boolean) {
    this.branchesEmpty.set(isEmpty);
  }

  onActivitiesEmpty(isEmpty: boolean) {
    this.activitiesEmpty.set(isEmpty);
  }

  isFiltersEmpty = computed(
    () =>
      this.branchesEmpty() ||
      (this.activitiesEmpty() && this.isActivityFilterUsed())
  );

  constructor() {
    effect(() => {
      this.languageService.currentLanguage();
      this.loadFilters();
    });
  }

  async loadFilters(): Promise<void> {
    this.loading.set(true);
    if (this.store.activitiesFiltersSelected()) {
      this.activitiesFiltersSelected.set(
        this.store.activitiesFiltersSelected()
      );
    }
    if (this.store.branchesFiltersSelected()) {
      this.branchesFiltersSelected.set(this.store.branchesFiltersSelected());
    }
    try {
      await this.store.loadVideoFilters();
      const filters: FilterSet[] = [
        this.store.activitiesFilters(),
        this.store.branchesFilters(),
      ];

      const filterSetSector = filters.find(
        (fs) => fs.filterSetType == FilterSetType.Sector
      );
      const filterSetActivity = filters.find(
        (fs) => fs.filterSetType == FilterSetType.Activity
      );

      filterSetSector.filtersCategories =
        this.filtersService.getAllFilterCategories(filterSetSector.filters);
      filterSetActivity.filtersCategories =
        this.filtersService.getAllFilterCategories(filterSetActivity.filters);
      filterSetActivity.filters = filterSetActivity.filters.filter(
        (x) => x.filters === null
      );
      filterSetSector.filters = filterSetSector.filters.filter(
        (x) => x.filters === null
      );

      this.store.updateSelectedFiltersTranslations();

      if (this.store.activitiesFiltersSelected()) {
        this.activitiesFiltersSelected.set(
          this.store.activitiesFiltersSelected()
        );
      }
      if (this.store.branchesFiltersSelected()) {
        this.branchesFiltersSelected.set(this.store.branchesFiltersSelected());
      }

      this.filters.set(filters);
    } catch (error) {
      if (this.errorModalService.shouldOpenErrorModal(error)) {
        this.errorModalService.openErrorModal();
      }
      this.filters.set([]);
    } finally {
      this.loading.set(false);
    }
  }

  checkAllBranchesFiltersAreDisableActivityFilter(): boolean {
    if (this.branchesFiltersSelected().filters.length === 0) {
      return true;
    }
    return this.branchesFiltersSelected().filters.every(
      (selectedFilter) =>
        selectedFilter.customProperties !== null &&
        "disableActivityFilter" in selectedFilter.customProperties &&
        selectedFilter.customProperties["disableActivityFilter"] === "true"
    );
  }

  updateFiltersSelected(filterUpdated: FilterSet) {
    this.isFavoriteSearchSuccessSaved.set(false);
    switch (filterUpdated.filterSetType) {
      case FilterSetType.Sector:
        this.branchesFiltersSelected.set(filterUpdated);

        if (this.checkAllBranchesFiltersAreDisableActivityFilter()) {
          this.isActivityFilterUsed.set(false);
        } else {
          this.isActivityFilterUsed.set(true);
        }

        break;
      case FilterSetType.Activity:
        this.activitiesFiltersSelected.set(filterUpdated);
        break;
    }
  }

  async saveSearch(searchName: string): Promise<void> {
    const createFavoriteFilterSearchRequest: CreateFavoriteFilterSearchRequestInterface =
      {
        name: searchName,
        searchType: SearchType.VideoSearch,
        research: {
          selectedFilters: [
            this.branchesFiltersSelected(),
            this.activitiesFiltersSelected(),
          ],
        },
      };

    try {
      await this.favoritesService.saveSearch(
        createFavoriteFilterSearchRequest
      );
      this.isFavoriteSearchSuccessSaved.set(true);
    } catch {
      this.favoriteStore.setFavoriteSearchesErrorModalOpen(true);
    } finally {
      this.isAddToFavoriteModalOpen.set(false);
    }
  }

  startResearch(): void {
    this.store.setFiltersSelected(
      this.branchesFiltersSelected(),
      this.isActivityFilterUsed() ? this.activitiesFiltersSelected() : null
    );

    this.router.navigate(["/mobility/videos-results"]);
  }

  openAddFavoriteModal(): void {
    this.isAddToFavoriteModalOpen.set(true);
  }

  closeAddFavoriteModal(): void {
    this.isAddToFavoriteModalOpen.set(false);
  }
}
